/**
 * The guest of this device. A guest is kept like a Google account, on its device (owner's choice, October
 * 2026; it lasted 5 hours before): the device keeps a random key, the account only its hash
 * (backend/auth.py), with the name and picture to show on « Continuer en tant que … » (Landing, JoinRoom).
 * Signed out, or the session lost, the player comes back to the same account with it. A new device, or
 * the app uninstalled, is a new guest.
 *
 * In the apps the key lives in Capacitor Preferences: iOS may empty a web view's localStorage when the
 * phone runs short of room. On the website Preferences writes to localStorage itself. A build without the
 * native plugin falls back to localStorage.
 */
import { useEffect, useState } from "react";
import { Preferences } from "@capacitor/preferences";
import { api, saveSessionToken } from "./api";

const STORE = "rami_guest";

async function load() {
  try {
    return (await Preferences.get({ key: STORE })).value;
  } catch {
    try { return localStorage.getItem(STORE); } catch { return null; }
  }
}

async function save(value) {
  try {
    if (value == null) await Preferences.remove({ key: STORE });
    else await Preferences.set({ key: STORE, value });
  } catch {
    try {
      if (value == null) localStorage.removeItem(STORE);
      else localStorage.setItem(STORE, value);
    } catch {}
  }
}

const base64url = (bytes) =>
  btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

/** 32 random bytes, 43 characters: the server's DEVICE_KEY. */
export const newDeviceKey = () => base64url(crypto.getRandomValues(new Uint8Array(32)));

/** The guest this device keeps: { key, user_id, name, picture }, or null. */
export async function knownGuest() {
  try {
    const g = JSON.parse(await load());
    return g?.key ? g : null;
  } catch {
    return null;
  }
}

const remember = (key, user) =>
  save(JSON.stringify({ key, user_id: user.user_id, name: user.name, picture: user.picture || null }));

export const forgetGuest = () => save(null);

/** A new guest for this device, which replaces the one it kept (deleted by the server, unless at a table). */
export async function createGuest(name) {
  const former = await knownGuest();
  const key = newDeviceKey();
  const { data } = await api.post("/auth/guest", { name, device_key: key, replaces: former?.key || undefined });
  saveSessionToken(data.session_token);
  await remember(key, data.user);
  return data.user;
}

/** Back to the device's guest. Throws; `err.gone` when that account no longer exists (it is then forgotten). */
export async function resumeGuest() {
  const known = await knownGuest();
  if (!known) throw Object.assign(new Error("no guest"), { gone: true });
  try {
    const { data } = await api.post("/auth/guest/resume", { device_key: known.key });
    saveSessionToken(data.session_token);
    await remember(known.key, data.user);
    return data.user;
  } catch (err) {
    if (err?.response?.status === 404) {
      await forgetGuest();
      err.gone = true;
    }
    throw err;
  }
}

/**
 * Called with the signed-in player (AuthContext): a guest made before the keys gets one, and the name and
 * picture kept for « Continuer en tant que … » follow the profile. A key kept for another account (two
 * guests in two tabs of one browser) is left alone.
 */
export async function keepGuest(user) {
  if (!user?.is_guest) return;
  const known = await knownGuest();
  if (!known) {
    const key = newDeviceKey();
    await api.post("/auth/guest/key", { device_key: key });
    await remember(key, user);
  } else if (known.user_id === user.user_id && (known.name !== user.name || known.picture !== (user.picture || null))) {
    await remember(known.key, user);
  }
}

/** [guest, setGuest, ready]: the device's guest, once read (the storage answers asynchronously). */
export function useKnownGuest() {
  const [guest, setGuest] = useState(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let live = true;
    knownGuest().then((g) => {
      if (!live) return;
      setGuest(g);
      setReady(true);
    });
    return () => { live = false; };
  }, []);
  return [guest, setGuest, ready];
}
